import * as React from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import MobileStepper from "@mui/material/MobileStepper";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { CircularProgress, ListItem, ListItemText } from "@mui/material";
import {
    pagesToShow,
    impactLevelInfo,
    setRiskConfig,
} from "../../app/riskLevel";
import { useGetRiskConfigQuery } from "../../services/riskSurface";
import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloud, faCloudBolt, faDewpoint, faEyeSlash, faIcicles, faRaindrops, faSnowflake, faTemperatureThreeQuarters, faWind } from "@fortawesome/sharp-duotone-light-svg-icons";
import { DRIVING_IMPACTS_LABEL } from "../UserVisibleStrings";
import ImpactIcon from "../risk/ImpactIcon";


export default function RiskLevelStepper( props: Readonly<{ level: number }> )
{
    const theme = useTheme();
    const [ activeStep, setActiveStep ] = React.useState( props.level );
    const maxSteps = pagesToShow.length;
    const { data, error, isLoading } = useGetRiskConfigQuery();
    useMemo( () => setRiskConfig( data, error, isLoading ), [ data, error, isLoading ] );

    const handleNext = () =>
    {
        setActiveStep( ( prevActiveStep ) => prevActiveStep + 1 );
    };

    const handleBack = () =>
    {
        setActiveStep( ( prevActiveStep ) => prevActiveStep - 1 );
    };
    const selectedInfo = impactLevelInfo[pagesToShow[activeStep]];
    if ( selectedInfo === undefined )
    {
        return <Box sx = {{ whiteSpace: "pre-wrap", flexGrow: 1 }}> "Undefined level " + pagesToShow[activeStep] </Box>;
    }
    const loading = ( key: number ) => <ListItem key = {key}><CircularProgress/></ListItem>;
    const details = [
        { icon: <FontAwesomeIcon icon = {faWind} size = "lg" fixedWidth/>, text: selectedInfo.windDetails, showLoading: true },
        { icon: <FontAwesomeIcon icon = {faRaindrops} size = "lg" fixedWidth/>, text: selectedInfo.rainDetails, showLoading: true },
        { icon: <FontAwesomeIcon icon = {faSnowflake} size = "lg" fixedWidth/>, text: selectedInfo.snowDetails, showLoading: true },
        { icon: <FontAwesomeIcon icon = {faIcicles} size = "lg" fixedWidth/>, text: selectedInfo.iceDetails, showLoading: true },
        { icon: <FontAwesomeIcon icon = {faTemperatureThreeQuarters} size = "lg" fixedWidth/>, text: selectedInfo.tempDetails, showLoading: true },
        { icon: <FontAwesomeIcon icon = {faCloudBolt} size = "lg" fixedWidth/>, text: selectedInfo.thunderstormDetails, showLoading: false },
        { icon: <FontAwesomeIcon icon = {faEyeSlash} size = "lg" fixedWidth/>, text: selectedInfo.visibilityDetails, showLoading: false },
        { icon: <FontAwesomeIcon icon = {faDewpoint} size = "lg" fixedWidth/>, text: selectedInfo.dewpointDetails, showLoading: false },
        { icon: <FontAwesomeIcon icon = {faCloud} size = "lg" fixedWidth/>, text: selectedInfo.cloudCoverDetails, showLoading: false }
    ];
    return (
        <React.Fragment>
            <Box sx = {{ whiteSpace: "pre-wrap", flexGrow: 1 }}>
                <Typography style = {{ fontSize: "24px", fontWeight: "bold", margin: "auto" }}>
                    {DRIVING_IMPACTS_LABEL}
                </Typography>
                <Paper
                    square
                    elevation = {0}
                    sx = {{
                        display: "flex",
                        alignItems: "center",
                        height: "auto",
                        pl: 2,
                        bgcolor: "background.default",
                    }}
                >
                    <ImpactIcon level = {pagesToShow[activeStep]} width = {34}/>
                    <Typography style = {{ overflowWrap: "break-word" }}>
                        {selectedInfo.tooltip}
                    </Typography>
                </Paper>
                <Box sx = {{ width: "100%", p: 2 }}>
                    {details.map( ( detail, index ) => detail.text !== undefined
                        ? (
                            detail.text !== "" &&
                            <ListItem key = {index}>
                                {detail.icon}
                                <ListItemText sx = {{ marginLeft: "10px" }}>{` ${detail.text}`}</ListItemText>
                            </ListItem>
                        ) : (
                            detail.showLoading ? loading( index ) : undefined
                        ) ) }
                </Box>
            </Box>
            <Box sx = {{ position: "absolute", bottom: "40px", width: "90%" }}>
                <MobileStepper
                    variant = "text"
                    steps = {maxSteps}
                    position = "static"
                    activeStep = {activeStep}
                    nextButton = {
                        <Button
                            size = "small"
                            onClick = {handleNext}
                            disabled = {activeStep === maxSteps - 1}
                        >
                            Next
                            {theme.direction === "rtl" ? (
                                <KeyboardArrowLeft/>
                            ) : (
                                <KeyboardArrowRight/>
                            )}
                        </Button>
                    }
                    backButton = {
                        <Button size = "small" onClick = {handleBack} disabled = {activeStep === 0}>
                            {theme.direction === "rtl" ? (
                                <KeyboardArrowRight/>
                            ) : (
                                <KeyboardArrowLeft/>
                            )}
                            Back
                        </Button>
                    }
                />
            </Box>
        </React.Fragment>
    );
}
